/**
 * LLM Diagnostics API Service
 *
 * Frontend API client for LLM diagnostics dashboard.
 */

import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Get authorization header
 * Uses x-auth-token to match server's authMiddleware
 */
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { 'x-auth-token': token } : {};
};

/**
 * Get overview statistics
 * @param {number} hours - Time range in hours (default: 24)
 */
export const getOverview = async (hours = 24) => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/overview`, {
    params: { hours },
    headers: getAuthHeader()
  });
  return response.data;
};

/**
 * Get paginated logs with filtering
 * @param {Object} filters - Filter options
 */
export const getLogs = async (filters = {}) => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/logs`, {
    params: filters,
    headers: getAuthHeader()
  });
  return response.data;
};

/**
 * Get detailed log entry
 * @param {string} logId - Log entry ID
 */
export const getLogDetail = async (logId) => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/logs/${logId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

/**
 * Get conversation-specific logs
 * @param {string} conversationId - Conversation ID
 */
export const getConversationLogs = async (conversationId) => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/conversation/${conversationId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

/**
 * Get error breakdown
 * @param {number} hours - Time range in hours
 */
export const getErrorBreakdown = async (hours = 24) => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/errors/breakdown`, {
    params: { hours },
    headers: getAuthHeader()
  });
  return response.data;
};

/**
 * Get trends data
 * @param {number} hours - Time range in hours
 * @param {string} interval - Grouping interval (hour/day)
 */
export const getTrends = async (hours = 24, interval = 'hour') => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/trends`, {
    params: { hours, interval },
    headers: getAuthHeader()
  });
  return response.data;
};

/**
 * Export logs as CSV
 * @param {Object} filters - Export filters
 */
export const exportLogs = async (filters = {}) => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/export`, {
    params: filters,
    headers: getAuthHeader(),
    responseType: 'blob'
  });

  // Trigger download
  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `llm-diagnostics-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Get LLM diagnostics settings
 */
export const getSettings = async () => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/settings`, {
    headers: getAuthHeader()
  });
  return response.data;
};

/**
 * Update logging level
 * @param {string} loggingLevel - 'off' | 'errors_only' | 'all'
 */
export const updateLoggingLevel = async (loggingLevel) => {
  const response = await axios.put(
    `${API_BASE}/admin/llm-diagnostics/settings/logging-level`,
    { loggingLevel },
    { headers: getAuthHeader() }
  );
  return response.data;
};

/**
 * Get storage usage estimate
 */
export const getStorageEstimate = async () => {
  const response = await axios.get(`${API_BASE}/admin/llm-diagnostics/storage-estimate`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export default {
  getOverview,
  getLogs,
  getLogDetail,
  getConversationLogs,
  getErrorBreakdown,
  getTrends,
  exportLogs,
  getSettings,
  updateLoggingLevel,
  getStorageEstimate
};
